import { useCallback, useRef, useState } from "react";
import { apiFetch, isApiError } from "@/api/client";
import { ep } from "@/api/endpoints";
import type { InsufficientDetails, SuggestionRequest, SuggestionResponse } from "@/api/types";
import { t } from "@/i18n/ko";
import { useAuthStore } from "@/stores/authStore";
import { useConfigStore } from "@/stores/configStore";
import { useUiStore } from "@/stores/uiStore";

type Options = { conversationId: string | undefined; onInsufficient: (d: InsufficientDetails) => void };

/**
 * 1.11 추천 답변. balance < pricing.suggestion → 부족 시트, 아니면 POST /suggestions {clientRequestId} → 칩 3개.
 * 402 → 시트(details), 실패(502 LLM_FAILED 포함, 서버가 환불) → Snackbar + 응답 balance. 결과는 다음 턴 전송 시 clear().
 */
export function useSuggestions({ conversationId, onInsufficient }: Options) {
  const [items, setItems] = useState<readonly string[]>([]);
  const [pending, setPending] = useState(false);
  const requestIdRef = useRef<string | null>(null);
  const showSnackbar = useUiStore((s) => s.showSnackbar);

  const request = useCallback(async () => {
    if (!conversationId || pending) return;
    const price = useConfigStore.getState().settings?.pricing.suggestion ?? 0;
    const balance = useAuthStore.getState().balance ?? 0;
    if (balance < price) {
      onInsufficient({ required: price, balance, model: null });
      return;
    }
    // 중복 탭 방지: 비행 중 키 고정, 성공하면 새 키.
    requestIdRef.current ??= crypto.randomUUID();
    const body: SuggestionRequest = { clientRequestId: requestIdRef.current };
    setPending(true);
    try {
      const res = await apiFetch<SuggestionResponse>(ep.suggestions(conversationId), { method: "POST", body });
      useAuthStore.getState().setBalance(res.balance);
      setItems(res.items);
      requestIdRef.current = null;
    } catch (e) {
      if (isApiError(e) && e.status === 402) {
        const d = (e.details ?? {}) as Partial<InsufficientDetails>;
        if (typeof d.balance === "number") useAuthStore.getState().setBalance(d.balance);
        onInsufficient({ required: d.required ?? price, balance: d.balance ?? balance, model: d.model ?? null });
        requestIdRef.current = null;
        return;
      }
      if (isApiError(e) && e.status === 409 && e.code === "SUGGESTION_DUPLICATE") {
        showSnackbar(t("error.generic"));
        return;
      }
      // 502 LLM_FAILED는 서버가 전액 환불(details.refunded). 새 키로 다시 요청할 수 있다.
      if (isApiError(e) && e.status === 502) requestIdRef.current = null;
      showSnackbar(isApiError(e) && e.code === "NETWORK" ? t("error.network") : t("chat.suggest.failed"));
    } finally {
      setPending(false);
    }
  }, [conversationId, onInsufficient, pending, showSnackbar]);

  const clear = useCallback(() => setItems([]), []);
  return { items, pending, request, clear };
}
